import React, { useEffect, useRef } from 'react';
import type { DiffPart } from '../utils/transcripts';
interface TranscriptEditorProps {
  /** Cumulative diff from the original upload to the live text. */
  parts: DiffPart[];
  /** Bumped when the content must be rebuilt from `parts` (save, undo, switch). */
  resetKey: number;
  onChange: (text: string) => void;
}

/** Marks a node as the struck-through record of deleted wording. */
const DELETED = 'data-deleted';

/**
 * The live transcript. Wording removed at any point stays visible and struck
 * through as a record, but is not part of the document: it sits in inert nodes
 * the caret skips, so the rest of the text stays fully editable.
 */
export function TranscriptEditor({
  parts,
  resetKey,
  onChange
}: TranscriptEditorProps) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = '';
    for (const part of parts) {
      if (part.kind === 'removed') {
        const span = document.createElement('span');
        span.setAttribute(DELETED, 'true');
        span.setAttribute('contenteditable', 'false');
        span.className = 'text-mute line-through select-none';
        span.textContent = part.text;
        el.appendChild(span);
      } else if (part.kind === 'added') {
        const span = document.createElement('span');
        span.className = 'bg-ink/[0.06] text-ink underline underline-offset-4';
        span.textContent = part.text;
        el.appendChild(span);
      } else {
        el.appendChild(document.createTextNode(part.text));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  /** The live document text — everything except the deleted record. */
  function readText(el: HTMLElement): string {
    let out = '';
    el.childNodes.forEach(node => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        if (element.hasAttribute(DELETED)) return;
        out += element.tagName === 'BR' ? '\n' : readText(element);
        return;
      }
      out += node.textContent ?? '';
    });
    return out;
  }
  return <div ref={ref} role="textbox" aria-multiline="true" aria-label="Transcript" contentEditable suppressContentEditableWarning spellCheck onInput={event => onChange(readText(event.currentTarget))} className="mt-5 min-h-[38rem] w-full whitespace-pre-wrap border border-rule bg-paperDeep px-6 py-6 font-display text-xl leading-relaxed text-charcoal focus:border-ink focus:outline-none sm:px-9 sm:py-8 sm:text-2xl" data-id="element-452" data-mp="k57hcig4oln7" />;
}